import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../images/renuit_logo.png';
import './Navbar.css';

const SERVICE_LINKS = [
  { to: '/services/freight-transportation', label: 'Freight Transportation' },
  { to: '/services/warehouse-storage', label: 'Warehouse & Storage' },
  { to: '/services/last-mile-delivery', label: 'Last-Mile Delivery' },
  { to: '/services/custom-logistics', label: 'Custom Logistics Plan' },
];

export const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (!isHomePage) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll(); // sync on mount in case page loads mid-scroll
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHomePage]);

  const toggleMenu = () => {
    const next = !menuOpen;
    setMenuOpen(next);
    document.body.classList.toggle('nav-open', next);
  };

  const close = () => {
    setMenuOpen(false);
    document.body.classList.remove('nav-open');
  };

  const isActive = (path: string) => location.pathname === path;
  const isServiceActive = location.pathname.startsWith('/services');

  return (
    <header
      id="site-nav"
      className={[menuOpen ? 'is-open' : '', scrolled ? 'is-scrolled' : ''].filter(Boolean).join(' ')}
    >
      <div className="nav-container">
        <Link to="/" className="nav-logo" aria-label="Renuit home" onClick={close}>
          <img src={logo} alt="Renuit Logistics" width="140" height="30" />
        </Link>

        <nav className="nav-links" role="navigation" aria-label="Main navigation">
          <ul className="nav-list">
            <li>
              <Link to="/" className={`nav-link${isActive('/') ? ' nav-link--active' : ''}`}>Home</Link>
            </li>
            <li>
              <Link to="/about" className={`nav-link${isActive('/about') ? ' nav-link--active' : ''}`}>About</Link>
            </li>
            <li className="nav-item nav-item--has-dropdown">
              <Link to="/services" className={`nav-link${isServiceActive ? ' nav-link--active' : ''}`}>
                Services <span className="nav-arrow" aria-hidden="true">▾</span>
              </Link>
              <ul className="nav-dropdown" aria-label="Services submenu">
                <li className="nav-dropdown__item nav-dropdown__item--all">
                  <Link to="/services" className="nav-dropdown__link" onClick={close}>
                    All Services
                  </Link>
                </li>
                {SERVICE_LINKS.map(({ to, label }) => (
                  <li key={to} className="nav-dropdown__item">
                    <Link to={to} className="nav-dropdown__link" onClick={close}>{label}</Link>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <Link to="/coverage" className={`nav-link${isActive('/coverage') ? ' nav-link--active' : ''}`}>Coverage</Link>
            </li>
          </ul>
        </nav>

        <div className="nav-end">
          <a href="tel:7866283731" className="nav-phone">(786) 628-3731</a>
          <Link to="/quote" className="nav-cta">Get a Quote</Link>
          <button
            className={`nav-hamburger${menuOpen ? ' is-open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      <div className="nav-drawer" aria-hidden={!menuOpen}>
        <ul className="nav-drawer-list">
          <li><Link to="/" className="nav-drawer-link" onClick={close}>Home</Link></li>
          <li><Link to="/about" className="nav-drawer-link" onClick={close}>About</Link></li>
          <li><Link to="/services" className="nav-drawer-link" onClick={close}>Services — Overview</Link></li>
          {SERVICE_LINKS.map(({ to, label }) => (
            <li key={to}>
              <Link to={to} className="nav-drawer-link nav-drawer-link--sub" onClick={close}>{label}</Link>
            </li>
          ))}
          <li><Link to="/coverage" className="nav-drawer-link" onClick={close}>Coverage</Link></li>
          <li>
            <Link to="/contact" className="nav-drawer-link" onClick={close}>Contact</Link>
          </li>
          <li>
            <Link to="/quote" className="nav-cta nav-cta--mobile" onClick={close}>Get a Quote</Link>
          </li>
        </ul>
      </div>

      {/* Sticky mobile CTA bar */}
      <div className="nav-mobile-cta" aria-hidden="true">
        <Link to="/quote" className="nav-mobile-cta__link" onClick={close}>Get a Free Quote →</Link>
      </div>
    </header>
  );
};
